import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { testSuitesApi } from '@/api/testSuites'
import { useTenantKey } from './useTenantKey'
import type { CreateTestSuitesRequest, PushTestSuiteRequest } from '@/types'

export function useTestSuiteDetails(projectId: string, testSuiteId: string) {
  const tenantKey = useTenantKey()

  return useQuery({
    queryKey: tenantKey('projects', projectId, 'test-suites', testSuiteId),
    queryFn: () => testSuitesApi.getDetails(projectId, testSuiteId),
    enabled: !!projectId && !!testSuiteId,
    staleTime: 0,
  })
}

export function useCreateTestSuites(projectId: string, testPlanId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (data: CreateTestSuitesRequest) =>
      testSuitesApi.create(projectId, testPlanId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'test-plans') })
    },
  })
}

export function useDeleteTestSuite(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (testSuiteId: string) => testSuitesApi.delete(projectId, testSuiteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'test-plans') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'test-suites') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items') })
    },
  })
}

export function usePushTestSuiteToDevOps(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: ({ testSuiteId, data }: { testSuiteId: string; data: PushTestSuiteRequest }) =>
      testSuitesApi.pushToDevOps(projectId, testSuiteId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'test-plans') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'test-suites') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'test-cases') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'work-items') })
    },
  })
}
